import { EnumSettingApply } from '../../../../../api/com/MetaPattern/APP/MDA/User/EnumSettingApply';
import { EnumSettingItemType } from '../../../../../api/com/MetaPattern/APP/MDA/User/EnumSettingItemType';
import { MDAUserService } from '../../../../../api/com/MetaPattern/APP/MDA/User/MDAUserService';
import { SelectGroup } from '../../../../../api/com/MetaPattern/APP/MDA/User/SelectGroup';
import { SettingGroup } from '../../../../../api/com/MetaPattern/APP/MDA/User/SettingGroup';
import { SettingItem } from '../../../../../api/com/MetaPattern/APP/MDA/User/SettingItem';
import { User } from '../../../../../api/com/MetaPattern/APP/MDA/User/User';
import { AccountInfoDTO } from '../../../../../api/com/MetaPattern/APP/MDA/User/AccountInfoDTO';
import { EnumDisplayLanguage } from '../../../../../api/com/MetaPattern/MDA/Designer/Base/EnumDisplayLanguage';
import { APP } from '../../../../../api/com/MetaPattern/MDA/MD/Base/APP';
import { IDEProgram } from './IDEProgram';
import { Dictionary } from './Util/Dictionary';
import { EnumStorageType, StorageLib } from './Util/StorageLib';
import { TreeNode } from './Util/TreeNode';

/** IDE的上下文 */
export class IDEContext {
  private static _instance = new IDEContext();
  private _users = new Array<User>();
  private _curUser: User = null;
  private _curAccountInfo: AccountInfoDTO = null;
  private _settings: Dictionary<string, string | number | string[]> = new Dictionary<string, string | number | string[]>();
  private StorageKeys = {
    ACCOUNT_KEY: 'AccountInfo',
    USER_KEY: 'UserInfo'
  };

  static get instance() {
    return this._instance;
  }

  get curAccountInfo(): AccountInfoDTO {
    return this._curAccountInfo;
  }

  async setCurAccountInfoAsync(value: AccountInfoDTO): Promise<void> {
    return new Promise((resolve, reject) => {
      if (value !== null) {
        StorageLib.instance.setItem(EnumStorageType.Session, this.StorageKeys.ACCOUNT_KEY, value);
        this._curAccountInfo = value;
        this.getUserInfoAsync(this._curAccountInfo.accountId).then(() => {
          resolve();
        });
      } else {
        StorageLib.instance.removeItem(EnumStorageType.Session, this.StorageKeys.ACCOUNT_KEY);
        this._curAccountInfo = null;
        this._curUser = null;
        resolve();
      }
    });
  }

  /** 获取当前用户身份 */
  get curUser(): User {
    return this._curUser;
  }

  /** 设置当前用户身份 */
  set curUser(value: User) {
    this._curUser = value;
    this.buildSettings();
  }

  /** 获取所有的用户身份 */
  get users(): Array<User> {
    return this._users;
  }

  get isLogin(): boolean {
    // if (this._curAccountInfo !== null)
    // const days = this.getSetting('Common:AutoSaveDays') as number;
    // const dayLength = 24 * 60 * 60 * 1000;
    // const loginTime = this._curAccountInfo.lastValidOn.getTime();
    // console.log(days);
    return this._curAccountInfo !== null;
  }

  /** 登出处理 */
  public logout() {
    this.setCurAccountInfoAsync(null);
  }

  // 初始化
  // 1. 恢复登录信息
  // 2. 恢复用户信息（当登录有效的时候）
  // 3. 构建用户设置信息
  public async initAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      const accountInfo = StorageLib.instance.getItem<any>(EnumStorageType.Session, this.StorageKeys.ACCOUNT_KEY);
      if (accountInfo !== null) {
        this._curAccountInfo = new AccountInfoDTO();
        this._curAccountInfo.accountId = accountInfo._accountId;
        this._curAccountInfo.email = accountInfo._email;
        // this._curAccountInfo.lastValidOn = accountInfo._lastValidOn;
        this._curAccountInfo.loginName = accountInfo._loginName;
        this._curAccountInfo.mobile = accountInfo._mobile;
        this._curAccountInfo.token = accountInfo._token;
        this.getUserInfoAsync(this._curAccountInfo.accountId).then(() => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  /** 根据编码获取用户的配置值 */
  public getSetting(code: string): string | number | string[] {
    if (this._settings.contains(code)) {
      return this._settings.getValue(code);
    }
    return '';
  }

  /** 获取显示语言，默认为中文 */
  public getDisplayLanguage(): EnumDisplayLanguage {
    const language = this.getSetting('Editor_View:ComponentLanguage') as string;
    if (language === 'English') {
      return EnumDisplayLanguage.English;
    }
    return EnumDisplayLanguage.Chinese; // 默认语言为中文
  }

  /** 设置设置项的值，如果存在则修改，不存在则添加 */
  public setSetting(code: string, value: string | number | string[]) {
    this._settings.setValue(code, value);
  }

  // 获取用户信息
  public async getUserInfoAsync(accountInfoId: string, curUserId = ''): Promise<void> {
    return new Promise((resolve, reject) => {
      MDAUserService.GetUserAndSettingsAsync(accountInfoId)
        .then((response: Array<User>) => {
          this._users = response;
          if (curUserId === '') {
            if (response.length === 1) {
              this._curUser = response[0];
              this.buildSettings();
              resolve();
            } else {
              resolve();
            }
          } else {
            response.forEach(user => {
              if (user.id === curUserId) {
                this._curUser = user;
                this.buildSettings();
                resolve();
              }
            });
          }
        });
    });
  }

  /**
   * 构建用户设置，如果用户为空则清空设置
   * Key：设置项的全编码
   * Value：设置项的值：
   * 1、用户未设置则用设置项默认值
   * 2、用户设置的话，选择类型使用选项的编码，多个选项使用数组，否则使用设置值
   */
  public buildSettings() {
    this._settings = new Dictionary<string, string | number | string[]>();
    const treeNodes: Array<TreeNode<SettingGroup>> = IDEProgram.instance.getSetting(EnumSettingApply.Global);
    const selectGroups = IDEProgram.instance.getSelectGroups();
    treeNodes.forEach(node => {
      node.data.settingItems.forEach(settingItem => {
        this.addSetting(settingItem, selectGroups);
      });
      node.data.children.forEach(child => {
        child.settingItems.forEach(settingItem => {
          this.addSetting(settingItem, selectGroups);
        });
      });
    });
  }

  /** 过滤只属于自己的APP列表 */
  public filterMyAPPs(apps: Array<APP>) {
    return apps.filter(app => app.aPPKey !== IDEProgram.instance.InfrastructureAPPKey || app.owner === IDEContext.instance.curUser.tenancy_ID);
    // return apps;
  }

  // 获取用户的设置值，如果用户未登录或者没有保存设置值，则返回设置项的缺省值
  private addSetting(settingItem: SettingItem, selectGroups: Array<SelectGroup>) {
    let itemValue: string | number | string[] = settingItem.defaultValue;
    if (IDEContext.instance.curUser !== null) {
      const userSetting = this._curUser.userSettings.find(item => item.settingItem_ID === settingItem.id);
      if (userSetting) {
        itemValue = userSetting.itemValue;
      }
    }
    if (settingItem.selectGroup_ID !== null) {
      const selectGroup = selectGroups.find(group => group.id === settingItem.selectGroup_ID);
      if (selectGroup && settingItem.dataType === EnumSettingItemType.SingleSelect) {
        const selectValues = Number(itemValue);
        const find = selectGroup.selectOptions.find(option => selectValues === option.optionValue);
        if (find) {
          itemValue = find.code;
        }
      } else if (selectGroup && settingItem.dataType === EnumSettingItemType.MultiSelect) {
        const result: string[] = [];
        const selectValues = Number(itemValue);
        selectGroup.selectOptions.forEach(option => {
          if ((selectValues & option.optionValue) > 0) {
            result.push(option.code);
          }
          itemValue = result;
        });
      }
    }
    if (!this._settings.contains(settingItem.code)) {
      this._settings.append(settingItem.code, itemValue);
    }
  }
}
